module Client.ResetPassword


open System
open Shared
open Elmish
open Illy.SafeStack
open Illy.SafeStack.Client
open Fable.React
open Fable.React.Props
open Shared.Zxcvbn
open Shared.PasswordReset.ResetPassword
open SafeStack.Client

type Model =
    {
        PasswordToken: Guid
        Password: ScorablePassword.Model
        ConfirmPassword: Text.Model
        IsTokenValid: bool
        Loading: bool
        Saving: bool
        IsMFA: bool
    }

type Msg =
    | InitDataLoaded of InitData
    | ApiError of ApiError
    | PasswordMsg of ScorablePassword.Msg
    | ConfirmPasswordMsg of Text.Msg
    | ResetResult of ROP.Result<unit, FailureReason>
    | UpdatePasswordClicked
    | StorageError of exn
    | EndNotification

module Initialisations =
    let initPassword (config: PasswordConfig) value =
        ScorablePassword.init 
            {ScorablePassword.Settings.defaults with Config = config} 
            value

    let initConfirmPassword value =
        {Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password}
        |> Text.init "ConfirmPassword" "re-type your new password" (Some "re-type your new password") 250. true value

module Validations = 
    open Illy.SafeStack.Validation
    open Shared.PasswordReset.ResetPassword.Validations
        
    let resetValidations (model: Model) = 
        {model with 
            Password = ScorablePassword.resetValidations model.Password
            ConfirmPassword = Text.resetValidations model.ConfirmPassword
        }

    let dispatchValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = 
                err 
                |> ValidationInfrastructure.bundleValidations 
                    (fun x -> 
                        (match x with 
                        | Password _ -> 0
                        | ConfirmPassword _ -> 1), ResetPasswordValidations.toString x)
                    2
            {model with 
                Password = ScorablePassword.setValidations model.Password errors.[0] None
                ConfirmPassword = Text.setValidations model.ConfirmPassword errors.[1] None
            }

let toDTO (m: Model) =
    {
        Password = ScorablePassword.toString m.Password
        ConfirmPassword = Text.toString m.ConfirmPassword
    }

let init passwordToken =
    {
        PasswordToken = passwordToken |> Option.defaultValue (Guid.NewGuid())
        IsTokenValid = passwordToken.IsSome
        Password = Initialisations.initPassword PasswordConfig.defaults None 
        ConfirmPassword = Initialisations.initConfirmPassword None 
        Loading = passwordToken.IsSome
        Saving = false
        IsMFA = false
    }, Cmd.batch [
        match passwordToken with
        | None -> ()
        | Some token -> 
            yield Api.unsecureCall<IResetPasswordApi,_,_,_> (fun api -> api.getInitData) token InitDataLoaded Msg.ApiError
    ] 

let update msg model =
    match msg with
    | Msg.ApiError err -> model, SideEffect.Event err
    | InitDataLoaded d ->
        {model with
            Password = Initialisations.initPassword d.PasswordConfig None
            IsTokenValid = d.IsTokenValid
            Loading = false
            IsMFA = d.IsMFA
        }, SideEffect.none
    | PasswordMsg cMsg ->
        let m, c = ScorablePassword.update cMsg model.Password
        {model with Password = m}, Cmd.map PasswordMsg c |> SideEffect.Cmd
    | ConfirmPasswordMsg cMsg ->
        let m, c = Text.update cMsg model.ConfirmPassword
        {model with ConfirmPassword = m}, Cmd.map ConfirmPasswordMsg c |> SideEffect.Cmd
    | UpdatePasswordClicked ->
        let dto = toDTO model
        let vRet =
            Validations.validateDTO
                Zxcvbn.scorePassword
                model.Password.Settings.Config
                dto

        if Validation.isSuccessful false vRet then
            {model with Saving = true},
            Api.unsecureCall<IResetPasswordApi,_,_,_> (fun api -> api.resetPassword model.PasswordToken) dto ResetResult ApiError
            |> SideEffect.Cmd
        else Validations.dispatchValidations model vRet, SideEffect.none
    | ResetResult ret ->
        match ret with
        | ROP.Success () ->
            let route = Routing.PageRoute.Login

            model,
            Cmd.batch [
                Notification.success "Password was reset successfully. Please log in using your new credentials." EndNotification
                route |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl
            ] |> SideEffect.Cmd
        | ROP.Failure InvalidToken ->
            {model with
                Saving = false
                IsTokenValid = false
            }, SideEffect.none
        | ROP.Failure (FailedValidations v) ->
            Validations.dispatchValidations {model with Saving = false} v, SideEffect.none 
    | StorageError exn ->
#if DEBUG
        printfn "There was a problem saving your login to the browser: %O" exn
#endif
        model, SideEffect.none
    | EndNotification -> 
        model, SideEffect.none

let renderInvalidTokenInfo =
    [
        h1 [] [
            str "Can not continue..."
        ]
        p [] [
            str "Your password could not be reset. This could be for a number of reasons:"
        ]
        ul [] [
            li [] [ str "You have mistyped a url." ]
            li [] [ str "Your password reset request has expired (you only have 24 hours to act upon a reset email)." ]
            li [] [ str "Your password has already been reset." ]
        ]
    ]

let renderEditContent model dispatch =
    [
        yield h1 [] [ str "Update your password"]
        yield! ScorablePassword.view model.Loading model.Password (PasswordMsg >> dispatch)
        yield Text.text model.Loading model.ConfirmPassword (ConfirmPasswordMsg >> dispatch)

        if model.Saving then yield jQueryUI.saveSpinner
        else yield UI.buttonWithDisabled model.Saving "Update password" UpdatePasswordClicked dispatch
    ]

let view model dispatch =
    div [
        Id "password-reset"
    ] [
        div [ Id "wrap" ] [
            form [ Id "form" ] [
                div [ Id "profile-management" ] [
                    if not model.Loading && not model.IsTokenValid then
                        yield! renderInvalidTokenInfo
                    else 
                        yield! renderEditContent model dispatch
                ]
            ]
        ]
    ]